<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { NODE_MIN_HEIGHT, NODE_WIDTH } from '../../constants/csmlLayout';
import { getNodeCategory } from '../../constants/csmlNodeCategories';

const props = defineProps({
  nodes: { type: Array, default: () => [] },
  heights: { type: Object, default: () => ({}) },
  offsetX: { type: Number, default: 0 },
  offsetY: { type: Number, default: 0 },
  zoom: { type: Number, default: 1 },
  viewportWidth: { type: Number, default: 0 },
  viewportHeight: { type: Number, default: 0 },
  open: { type: Boolean, default: true },
});

const emit = defineEmits(['pan', 'update:open']);

const { t } = useI18n();

const MAP_WIDTH = 176;
const MAP_HEIGHT = 112;
const PADDING = 40;

// Fill classes spelled out in full so Tailwind can see them.
const FILL_BY_CATEGORY = {
  start: 'fill-n-teal-9',
  message: 'fill-n-blue-9',
  ask: 'fill-n-violet-9',
  logic: 'fill-n-amber-9',
  integration: 'fill-n-iris-9',
};

const rects = computed(() =>
  props.nodes.map(node => ({
    id: node.id,
    x: node.x,
    y: node.y,
    width: NODE_WIDTH,
    height: props.heights[node.id] || NODE_MIN_HEIGHT,
    fill: FILL_BY_CATEGORY[getNodeCategory(node.type)],
  }))
);

// What the main canvas currently shows, in flow coordinates.
const view = computed(() => ({
  x: -props.offsetX / props.zoom,
  y: -props.offsetY / props.zoom,
  width: props.viewportWidth / props.zoom,
  height: props.viewportHeight / props.zoom,
}));

// The map frames the flow and the current view together, so the view
// rectangle never slides off the edge of the map.
const bounds = computed(() => {
  const boxes = [...rects.value, view.value];
  const minX = Math.min(...boxes.map(b => b.x)) - PADDING;
  const minY = Math.min(...boxes.map(b => b.y)) - PADDING;
  const maxX = Math.max(...boxes.map(b => b.x + b.width)) + PADDING;
  const maxY = Math.max(...boxes.map(b => b.y + b.height)) + PADDING;
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
});

const viewBox = computed(() => {
  const { x, y, width, height } = bounds.value;
  return `${x} ${y} ${width} ${height}`;
});

const svgRef = ref(null);
let dragging = false;

// Turns a pointer position over the map into the flow point under it, then
// asks the canvas to centre that point.
const panTo = event => {
  const rect = svgRef.value.getBoundingClientRect();
  const { x, y, width, height } = bounds.value;
  const scale = Math.min(rect.width / width, rect.height / height);
  const drawnWidth = width * scale;
  const drawnHeight = height * scale;
  const localX = event.clientX - rect.left - (rect.width - drawnWidth) / 2;
  const localY = event.clientY - rect.top - (rect.height - drawnHeight) / 2;
  const flowX = x + localX / scale;
  const flowY = y + localY / scale;
  emit('pan', {
    x: props.viewportWidth / 2 - flowX * props.zoom,
    y: props.viewportHeight / 2 - flowY * props.zoom,
  });
};

const onMove = event => {
  if (dragging) panTo(event);
};

const stopDrag = () => {
  dragging = false;
  window.removeEventListener('mousemove', onMove);
  window.removeEventListener('mouseup', stopDrag);
};

const onDown = event => {
  dragging = true;
  panTo(event);
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', stopDrag);
};

onBeforeUnmount(stopDrag);
</script>

<template>
  <div
    class="flex flex-col items-start gap-1.5"
    @mousedown.stop
    @click.stop
    @wheel.stop
  >
    <Button
      v-if="!open"
      size="sm"
      variant="faded"
      color="slate"
      icon="i-lucide-map"
      is-icon-only
      :title="t('AGENT_BOTS.VISUAL_BUILDER.MINIMAP.SHOW')"
      @click="emit('update:open', true)"
    />
    <div
      v-else
      class="relative overflow-hidden border shadow-sm rounded-xl border-n-weak bg-n-solid-1/95 backdrop-blur"
    >
      <svg
        ref="svgRef"
        class="block cursor-pointer"
        :width="MAP_WIDTH"
        :height="MAP_HEIGHT"
        :viewBox="viewBox"
        preserveAspectRatio="xMidYMid meet"
        @mousedown.prevent="onDown"
      >
        <rect
          v-for="rect in rects"
          :key="rect.id"
          :x="rect.x"
          :y="rect.y"
          :width="rect.width"
          :height="rect.height"
          rx="12"
          class="opacity-80"
          :class="rect.fill"
        />
        <rect
          :x="view.x"
          :y="view.y"
          :width="view.width"
          :height="view.height"
          class="fill-n-brand/10 stroke-n-brand"
          stroke-width="6"
        />
      </svg>
      <Button
        size="xs"
        variant="ghost"
        color="slate"
        icon="i-lucide-x"
        is-icon-only
        class="absolute top-0.5 end-0.5"
        :title="t('AGENT_BOTS.VISUAL_BUILDER.MINIMAP.HIDE')"
        @click="emit('update:open', false)"
      />
    </div>
  </div>
</template>
